import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { supabase } from './supabaseClient';
import {
  Container, Typography, Box, CircularProgress, Alert,
  Breadcrumbs, Link, Paper, Grid, List, ListItem,
  ListItemButton, ListItemText, Divider, TextField, Button,
  Switch, FormControlLabel, MenuItem, FormControl, InputLabel, Select, Slider,
  IconButton, Tooltip, Chip
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SaveIcon from '@mui/icons-material/Save';
import ChatIcon from '@mui/icons-material/Chat';
import MemoryIcon from '@mui/icons-material/Memory';
import InfoIcon from '@mui/icons-material/Info';
import SummarizeIcon from '@mui/icons-material/Summarize';
import PersonIcon from '@mui/icons-material/Person';
import FlowDetailsModal from './components/FlowDetailsModal';

// Constants from UserSettingsSidebar
const DEFAULT_DIRECT_SYSTEM_PROMPT = "You are a helpful AI assistant. Answer the user's question directly.";

// Build grouped models map from llm_models rows
const toGroupedModels = (rows) => {
  const map = {};
  (rows || []).forEach(r => {
    const provider = (r.provider || '').trim();
    const ident = r.model_identifier;
    if (!provider || !ident) return;
    const label = r.display_label || ident;
    if (!map[provider]) map[provider] = [];
    map[provider].push({ value: ident, label });
  });
  Object.keys(map).forEach(p => map[p].sort((a, b) => a.label.localeCompare(b.label)));
  return map;
};

const findProviderForModel = (grouped, modelValue) => {
  for (const provider in grouped) {
    if ((grouped[provider] || []).some(model => model.value === modelValue)) {
      return provider;
    }
  }
  return null;
};


const safeParseFloat = (value) => (value === '' || value === null || value === undefined) ? null : parseFloat(value);
const safeParseInt = (value) => (value === '' || value === null || value === undefined) ? null : parseInt(value, 10);

const SessionSettingsPage = () => {
  const { userId } = useParams();
  console.log('[SessionSettingsPage] Component rendered with userId from URL:', userId); // DEBUGGING
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [settings, setSettings] = useState({});
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // New state for right-pane view and chat messages
  const [rightPaneView, setRightPaneView] = useState('none'); // 'none', 'settings', 'chat_messages', 'memories'
  const [chatMessages, setChatMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  const [memories, setMemories] = useState([]);
  const [loadingMemories, setLoadingMemories] = useState(false);
  const [memoriesError, setMemoriesError] = useState(null);
  // New state for rolling summary and profile (Phase 2, 4, 5)
  const [rollingSummary, setRollingSummary] = useState(null);
  const [loadingRollingSummary, setLoadingRollingSummary] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [groupedLlmModels, setGroupedLlmModels] = useState({});

  // Flow Details Modal state
  const [flowModalOpen, setFlowModalOpen] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const providerOptions = useMemo(() => Object.keys(groupedLlmModels), [groupedLlmModels]);

  // State from UserSettingsSidebar
  const [selectedProvider, setSelectedProvider] = useState('');

  // State for embedding models from database
  const [embeddingModels, setEmbeddingModels] = useState([]);

  const availableModels = useMemo(() => {
    return selectedProvider ? (groupedLlmModels[selectedProvider] || []) : [];
  }, [selectedProvider, groupedLlmModels]);

  const fetchSessions = useCallback(async () => {
    try {
      setError(null);
      setLoadingSessions(true);
      console.log('[SessionSettingsPage] Fetching sessions for user ID:', userId);

      // We now use an RPC call to bypass RLS for the admin panel.
      const { data, error } = await supabase
        .rpc('get_sessions_for_user_admin', { p_user_id: userId });

      if (error) throw error;
      console.log('[SessionSettingsPage] Supabase response for sessions:', { data, error });
      setSessions(data);
    } catch (err) {
      setError(`Failed to fetch sessions: ${err.message}`);
      console.error('[SessionSettingsPage] Error fetching sessions:', err);
    } finally {
      setLoadingSessions(false);
    }
  }, [userId]);

  const fetchSettingsForSession = useCallback(async (sessionId) => {
    try {
      setError(null);
      setLoadingSettings(true);

      // Use the new RPC to fetch settings, bypassing RLS for the admin.
      const { data: rpcData, error } = await supabase
        .rpc('get_session_settings_admin', { p_session_id: sessionId });

      if (error) throw error;

      // The RPC returns an array of settings objects. We expect 0 or 1.
      const currentSettings = (rpcData && rpcData.length > 0) ? rpcData[0] : {};

      // Process and set defaults similar to UserSettingsSidebar
      const processedSettings = { ...currentSettings };

      if (processedSettings.temperature !== undefined) {
        processedSettings.temperature = String(parseFloat(processedSettings.temperature).toFixed(1));
      } else {
        processedSettings.temperature = '0.0';
      }
      if (typeof processedSettings.enable_grounding_check !== 'boolean') {
        processedSettings.enable_grounding_check = false;
      }
      const convertedGST = parseFloat(processedSettings.grounding_similarity_threshold);
      processedSettings.grounding_similarity_threshold = (!isNaN(convertedGST) && convertedGST >= 0.0 && convertedGST <= 1.0) ? convertedGST : 0.4;

      const numericFieldsDefaults = { top_p: 1.0, total_chunks: 50, answer_max_token: 512, top_k: 15, cosine_similarity_threshold: 0.3 };
      for (const field in numericFieldsDefaults) {
        if (processedSettings[field] === undefined || processedSettings[field] === null) {
          processedSettings[field] = numericFieldsDefaults[field];
        }
      }

      setSettings(processedSettings);

      const initialProvider = findProviderForModel(groupedLlmModels, processedSettings.llm_model);
      if (initialProvider) {
        setSelectedProvider(initialProvider);
      } else {
        setSelectedProvider(providerOptions[0] || '');
      }

    } catch (err) {
      setError(`Failed to fetch settings: ${err.message}`);
    } finally {
      setLoadingSettings(false);
    }
  }, []);

  const fetchMessagesForSession = useCallback(async (sessionId) => {
    try {
      setMessagesError(null);
      setLoadingMessages(true);
      const { data, error } = await supabase
        .rpc('get_chat_messages_admin', { p_session_id: sessionId });
      if (error) throw error;
      setChatMessages(data || []);
    } catch (err) {
      setMessagesError(`Failed to fetch messages: ${err.message}`);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  const fetchMemoriesForSession = useCallback(async (sessionId) => {
    try {
      setMemoriesError(null);
      setLoadingMemories(true);
      const { data, error } = await supabase
        .rpc('get_mem0_memories_admin', { p_session_id: sessionId });
      if (error) throw error;
      setMemories(data || []);
    } catch (err) {
      setMemoriesError(`Failed to fetch memories: ${err.message}`);
    } finally {
      setLoadingMemories(false);
    }
  }, []);

  // Fetch rolling summary for a session (Phase 2)
  const fetchRollingSummaryForSession = useCallback(async (sessionId) => {
    try {
      setLoadingRollingSummary(true);
      const { data, error } = await supabase
        .from('session_rolling_summaries')
        .select('summary, message_range_start, message_range_end, token_count, profile_data, profile_extracted_at, updated_at')
        .eq('session_id', sessionId)
        .single();
      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
      setRollingSummary(data || null);
      setProfileData(data?.profile_data || null);
    } catch (err) {
      console.error('Failed to fetch rolling summary:', err.message);
      setRollingSummary(null);
      setProfileData(null);
    } finally {
      setLoadingRollingSummary(false);
      setLoadingProfile(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Load active LLM models dynamically
  // Fetch embedding models from database
  useEffect(() => {
    const loadEmbeddingModels = async () => {
      try {
        const { data, error } = await supabase
          .from('embedding_models')
          .select('model_identifier, display_label, provider')
          .eq('is_active', true)
          .order('provider', { ascending: true })
          .order('display_label', { ascending: true });

        if (error) throw error;

        setEmbeddingModels((data || []).map(row => ({
          value: row.model_identifier,
          label: row.display_label || row.model_identifier
        })));
      } catch (err) {
        console.warn('[SessionSettingsPage] Failed to load embedding_models:', err.message);
        setEmbeddingModels([]);
      }
    };
    loadEmbeddingModels();
  }, []);

  useEffect(() => {
    const loadModels = async () => {
      try {
        const { data, error } = await supabase
          .from('llm_models')
          .select('model_identifier, provider, display_label, is_active')
          .eq('is_active', true);
        if (error) throw error;
        setGroupedLlmModels(toGroupedModels(data || []));
      } catch (err) {
        console.warn('Failed to load llm_models:', err.message);
        setGroupedLlmModels({});
      }
    };
    loadModels();
  }, []);

  // When models load or llm_model changes, align provider selection
  useEffect(() => {
    if (!settings || !settings.llm_model) {
      if (!selectedProvider && providerOptions[0]) setSelectedProvider(providerOptions[0]);
      return;
    }
    const prov = findProviderForModel(groupedLlmModels, settings.llm_model);
    if (prov && prov !== selectedProvider) setSelectedProvider(prov);
  }, [groupedLlmModels, settings.llm_model, providerOptions, selectedProvider]);

  const handleSessionSelect = (session) => {
    setSelectedSession(session);
    setSaveSuccess(false);
    setRightPaneView('settings');
    fetchSettingsForSession(session.id);
  };

  const handleShowMessages = (session) => {
    setSelectedSession(session);
    setRightPaneView('chat_messages');
    fetchMessagesForSession(session.id);
  };

  const handleShowMemories = (session) => {
    setSelectedSession(session);
    setRightPaneView('memories');
    fetchMemoriesForSession(session.id);
  };

  const handleShowRollingSummary = (session) => {
    setSelectedSession(session);
    setRightPaneView('rolling_summary');
    fetchRollingSummaryForSession(session.id);
  };

  const handleShowProfile = (session) => {
    setSelectedSession(session);
    setRightPaneView('profile');
    fetchRollingSummaryForSession(session.id);
  };

  const handleShowFlowDetails = (messageId) => {
    setSelectedMessageId(messageId);
    setFlowModalOpen(true);
  };

  const handleCloseFlowModal = () => {
    setFlowModalOpen(false);
    setSelectedMessageId(null);
  };

  const getAuthToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token;
  };

  // Generic handler for most inputs
  const handleChange = (e, sliderValue) => {
    const { name, value, type, checked } = e.target;
    let newValue;
    if (name === 'grounding_similarity_threshold' || name === 'temperature') {
      newValue = sliderValue !== undefined ? sliderValue : parseFloat(value);
    } else if (type === 'checkbox') {
      newValue = checked;
    } else {
      newValue = value;
    }
    setSettings((prev) => ({ ...prev, [name]: newValue }));
  };

  // Provider selection handler
  const handleProviderChange = (e) => {
    const newProvider = e.target.value;
    setSelectedProvider(newProvider);
    const modelsForNewProvider = groupedLlmModels[newProvider] || [];
    setSettings(prev => ({
      ...prev,
      llm_model: modelsForNewProvider.length > 0 ? modelsForNewProvider[0].value : ''
    }));
  };

  // Model selection handler
  const handleModelChange = (e) => {
    setSettings(prev => ({ ...prev, llm_model: e.target.value }));
  };


  const handleSaveChanges = async () => {
    if (!selectedSession) return;
    setIsSaving(true);
    setError(null);
    setSaveSuccess(false);

    const payload = {
      p_session_id: selectedSession.id,
      p_embedding_model: settings.embedding_model,
      p_system_prompt: settings.system_prompt,
      p_llm_model: settings.llm_model,
      p_rag_mode: settings.rag_mode,
      p_direct_system_prompt: settings.direct_system_prompt,
      p_temperature: safeParseFloat(settings.temperature),
      p_top_p: safeParseFloat(settings.top_p),
      p_total_chunks: safeParseInt(settings.total_chunks),
      p_answer_max_token: safeParseInt(settings.answer_max_token),
      p_top_k: safeParseInt(settings.top_k),
      p_cosine_similarity_threshold: safeParseFloat(settings.cosine_similarity_threshold),
      p_enable_grounding_check: settings.enable_grounding_check,
      p_grounding_similarity_threshold: safeParseFloat(settings.grounding_similarity_threshold),
      p_enable_reranker: settings.enable_reranker,
      p_faq_direct_answer: settings.faq_direct_answer || false,
    };

    try {
      const { error } = await supabase
        .rpc('update_session_settings_admin', payload);

      if (error) {
        // More specific error for upsert failure
        if (error.message.includes('violates foreign key constraint')) {
          setError(`Failed to save settings: ${error.message}`);
        } else {
          throw error;
        }
      } else {
        setSaveSuccess(true);
        fetchSettingsForSession(selectedSession.id);
      }
    } catch (err) {
      setError(`Failed to save settings: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link component={RouterLink} underline="hover" color="inherit" to="/admin">Admin Panel</Link>
        <Link component={RouterLink} underline="hover" color="inherit" to={`/admin/user/${userId}`}>User Details</Link>
        <Typography color="text.primary">Manage Session Settings & Memories & Chat Messages</Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom>Manage Session Settings</Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Typography variant="h6" gutterBottom>User's Sessions</Typography>
          <Paper sx={{ height: '70vh', overflow: 'auto' }}>
            {loadingSessions ? <CircularProgress /> : error ? <Alert severity="error">{error}</Alert> : (
              <List>
                {sessions.map(session => (
                  <ListItem
                    key={session.id}
                    disablePadding
                    secondaryAction={
                      <>
                        <Tooltip title="MCP Long-term Memories">
                          <IconButton edge="end" aria-label="memories" onClick={(e) => { e.stopPropagation(); handleShowMemories(session); }} sx={{ mr: 0.5 }}>
                            <MemoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Rolling Summary">
                          <IconButton edge="end" aria-label="summary" onClick={(e) => { e.stopPropagation(); handleShowRollingSummary(session); }} sx={{ mr: 0.5 }}>
                            <SummarizeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Profile Data">
                          <IconButton edge="end" aria-label="profile" onClick={(e) => { e.stopPropagation(); handleShowProfile(session); }} sx={{ mr: 0.5 }}>
                            <PersonIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Chat Messages">
                          <IconButton edge="end" aria-label="comments" onClick={(e) => { e.stopPropagation(); handleShowMessages(session); }}>
                            <ChatIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    }
                  >
                    <ListItemButton selected={selectedSession?.id === session.id} onClick={() => handleSessionSelect(session)}>
                      <ListItemText primary={session.name || 'Untitled Session'} secondary={`ID: ${session.id.substring(0, 8)}...`} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={9}>
          <Typography variant="h6" gutterBottom>
            {rightPaneView === 'none' && 'No session selected'}
            {rightPaneView === 'settings' && `Settings for: ${selectedSession ? selectedSession.name || 'Untitled Session' : ''}`}
            {rightPaneView === 'chat_messages' && `Messages for: ${selectedSession ? selectedSession.name || 'Untitled Session' : ''}`}
            {rightPaneView === 'memories' && `MCP Memories for: ${selectedSession ? selectedSession.name || 'Untitled Session' : ''}`}
            {rightPaneView === 'rolling_summary' && `Rolling Summary for: ${selectedSession ? selectedSession.name || 'Untitled Session' : ''}`}
            {rightPaneView === 'profile' && `Profile Data for: ${selectedSession ? selectedSession.name || 'Untitled Session' : ''}`}
          </Typography>
          <Paper sx={{ p: 3, height: '70vh', overflow: 'auto' }}>
            {rightPaneView === 'none' && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography color="textSecondary">Select a session to view settings, or click the chat icon to view messages.</Typography>
              </Box>
            )}

            {rightPaneView === 'settings' && !loadingSettings && selectedSession && (
              <Box>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {saveSuccess && <Alert severity="success" sx={{ mb: 2 }}>Settings saved successfully!</Alert>}

                <TextField select fullWidth margin="normal" label="Embedding Model" name="embedding_model" value={settings.embedding_model || ''} onChange={handleChange}>
                  {embeddingModels.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>

                <TextField fullWidth margin="normal" label="System Prompt" name="system_prompt" value={settings.system_prompt || ''} onChange={handleChange} multiline rows={6} />

                <FormControl fullWidth margin="normal">
                  <InputLabel>LLM Provider</InputLabel>
                  <Select value={selectedProvider} label="LLM Provider" onChange={handleProviderChange}>
                    {providerOptions.map((provider) => (
                      <MenuItem key={provider} value={provider}>{provider}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth margin="normal" disabled={!selectedProvider}>
                  <InputLabel>LLM Model</InputLabel>
                  <Select name="llm_model" value={settings.llm_model || ''} label="LLM Model" onChange={handleModelChange}>
                    {availableModels.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ pl: 1, pr: 1, mt: 2 }}>
                  <Typography gutterBottom variant="body2">Creativity (Temperature): {parseFloat(settings.temperature || 0).toFixed(1)}</Typography>
                  <Slider name="temperature" value={parseFloat(settings.temperature || 0)} onChange={handleChange} step={0.1} marks min={0.0} max={2.0} valueLabelDisplay="auto" />
                </Box>

                <TextField select fullWidth margin="normal" label="RAG Strategy" name="rag_mode" value={settings.rag_mode || ''} onChange={handleChange} helperText="Similarity Filter is faster, Reranker may be more relevant.">
                  <MenuItem value="similarity_filter">Similarity Filter (Default)</MenuItem>
                  <MenuItem value="reranker">Reranker (Slower, Potentially Better)</MenuItem>
                  <MenuItem value="two_stage_summary">Two-Stage (Summaries First)</MenuItem>
                </TextField>

                <FormControlLabel control={<Switch checked={settings.enable_reranker || false} onChange={handleChange} name="enable_reranker" />} label="Enable Reranker (Improves Relevance)" sx={{ mt: 2, display: 'block' }} />
                <Typography variant="caption" display="block" gutterBottom sx={{ pl: 1 }}>Disabling may speed up responses but potentially reduce answer quality.</Typography>

                <TextField fullWidth margin="normal" label="Direct Mode System Prompt" name="direct_system_prompt" value={(settings.direct_system_prompt || '').trim() ? settings.direct_system_prompt : DEFAULT_DIRECT_SYSTEM_PROMPT} onChange={handleChange} multiline rows={4} helperText="System prompt used when 'Direct Mode' is enabled in chat. Leave blank for default." sx={{ mt: 1 }} />

                <FormControlLabel control={<Switch checked={settings.enable_grounding_check || false} onChange={handleChange} name="enable_grounding_check" />} label="Enable Answer Grounding Check" sx={{ mt: 1, display: 'block' }} />
                <Typography variant="caption" display="block" gutterBottom sx={{ pl: 1 }}>Checks if the answer likely came from the context (adds slight delay).</Typography>

                {settings.enable_grounding_check && (
                  <Box sx={{ pl: 1, pr: 1, mt: 1 }}>
                    <Typography gutterBottom variant="caption">Grounding Threshold: {(settings.grounding_similarity_threshold || 0).toFixed(2)}</Typography>
                    <Slider name="grounding_similarity_threshold" value={settings.grounding_similarity_threshold || 0} onChange={handleChange} step={0.05} marks min={0.0} max={1.0} valueLabelDisplay="auto" />
                  </Box>
                )}

                <FormControlLabel control={<Switch checked={settings.faq_direct_answer || false} onChange={handleChange} name="faq_direct_answer" />} label="FAQ Direct Answer (Instant Responses)" sx={{ mt: 2, display: 'block' }} />
                <Typography variant="caption" display="block" gutterBottom sx={{ pl: 1 }}>Return FAQ answers instantly without LLM processing (~2s vs ~13s). Disable for more natural reformulation.</Typography>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="contained" startIcon={<SaveIcon />} disabled={isSaving} onClick={handleSaveChanges}>
                    {isSaving ? <CircularProgress size={24} /> : 'Save Changes'}
                  </Button>
                </Box>
              </Box>
            )}

            {rightPaneView === 'chat_messages' && (
              loadingMessages ? <CircularProgress /> : messagesError ? <Alert severity="error">{messagesError}</Alert> :
                <List>
                  {chatMessages.length > 0 ? chatMessages.map(message => (
                    <ListItem key={message.id} alignItems="flex-start" sx={{
                      bgcolor: message.role === 'user' ? 'action.hover' : 'background.paper',
                      borderRadius: 2,
                      mb: 1
                    }}>
                      <ListItemText
                        primary={<strong>{message.role === 'user' ? 'User' : 'AI'}</strong>}
                        secondary={
                          <>
                            <Typography component="span" variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap' }}>
                              {message.content}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                              {new Date(message.created_at).toLocaleString()} {message.model && `(${message.model})`}
                            </Typography>
                            {message.role === 'assistant' && (
                              <Box sx={{ mt: 1 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<InfoIcon />}
                                  onClick={() => handleShowFlowDetails(message.id)}
                                  sx={{ textTransform: 'none' }}
                                >
                                  Flow Details
                                </Button>
                              </Box>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                  )) : <Typography color="textSecondary">No messages in this session.</Typography>}
                </List>
            )}

            {rightPaneView === 'memories' && (
              loadingMemories ? <CircularProgress /> : memoriesError ? <Alert severity="error">{memoriesError}</Alert> :
                <List>
                  {memories.length > 0 ? memories.map(memory => (
                    <ListItem key={memory.id} alignItems="flex-start" sx={{
                      bgcolor: memory.metadata?.type === 'session_summary' ? 'info.light' :
                        memory.metadata?.role === 'user' ? 'action.hover' : 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      mb: 1
                    }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <strong>Memory ({memory.metadata?.role || 'N/A'})</strong>
                            {memory.metadata?.type && (
                              <Chip label={memory.metadata.type} size="small" color="info" variant="outlined" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography component="span" variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap' }}>
                            {memory.metadata?.data || 'No data content.'}
                          </Typography>
                        }
                      />
                    </ListItem>
                  )) : <Typography color="textSecondary">No memories found for this session.</Typography>}
                </List>
            )}

            {rightPaneView === 'rolling_summary' && (
              loadingRollingSummary ? <CircularProgress /> :
                <Box>
                  {rollingSummary ? (
                    <>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Covers messages {rollingSummary.message_range_start}-{rollingSummary.message_range_end}
                        ({rollingSummary.token_count || '?'} tokens)
                        • Updated: {new Date(rollingSummary.updated_at).toLocaleString()}
                      </Alert>
                      <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50', whiteSpace: 'pre-wrap' }}>
                        <Typography variant="body1">{rollingSummary.summary}</Typography>
                      </Paper>
                    </>
                  ) : (
                    <Alert severity="warning">
                      No rolling summary yet. Summary is generated when session has more than 15 messages.
                    </Alert>
                  )}
                </Box>
            )}

            {rightPaneView === 'profile' && (
              loadingProfile ? <CircularProgress /> :
                <Box>
                  {profileData && Object.keys(profileData).length > 0 ? (
                    <>
                      <Alert severity="success" sx={{ mb: 2 }}>
                        Profile extracted from conversation summary.
                      </Alert>

                      {profileData.preferences && Object.keys(profileData.preferences).length > 0 && (
                        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                          <Typography variant="subtitle1" gutterBottom><strong>Preferences</strong></Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {Object.entries(profileData.preferences).map(([key, value]) => (
                              <Chip
                                key={key}
                                label={`${key}: ${Array.isArray(value) ? value.join(', ') : value}`}
                                variant="outlined"
                                color="primary"
                              />
                            ))}
                          </Box>
                        </Paper>
                      )}

                      {profileData.behavior && Object.keys(profileData.behavior).length > 0 && (
                        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                          <Typography variant="subtitle1" gutterBottom><strong>Behavior</strong></Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {Object.entries(profileData.behavior).map(([key, value]) => (
                              <Chip key={key} label={`${key}: ${value}`} variant="outlined" color="secondary" />
                            ))}
                          </Box>
                        </Paper>
                      )}

                      {profileData.engagement && Object.keys(profileData.engagement).length > 0 && (
                        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                          <Typography variant="subtitle1" gutterBottom><strong>Engagement</strong></Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {Object.entries(profileData.engagement).map(([key, value]) => (
                              <Chip key={key} label={`${key}: ${value}`} variant="outlined" color="success" />
                            ))}
                          </Box>
                        </Paper>
                      )}

                      {profileData.confidence && Object.keys(profileData.confidence).length > 0 && (
                        <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="subtitle2" gutterBottom>Confidence Scores</Typography>
                          <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                            {JSON.stringify(profileData.confidence, null, 2)}
                          </Typography>
                        </Paper>
                      )}
                    </>
                  ) : (
                    <Alert severity="warning">
                      No profile data yet. Profile is extracted after rolling summary is generated (15+ messages).
                    </Alert>
                  )}
                </Box>
            )}

            {(loadingSettings || loadingMessages || loadingMemories || loadingRollingSummary || loadingProfile) && <CircularProgress />}
          </Paper>
        </Grid>
      </Grid>

      <FlowDetailsModal
        open={flowModalOpen}
        onClose={handleCloseFlowModal}
        messageId={selectedMessageId}
        getAuthToken={getAuthToken}
      />
    </Container>
  );
};

export default SessionSettingsPage; 